import  { useState, useEffect } from 'react';
import { useFolders } from '@/hooks/useFolders';
import Button from '../shared/Button';

interface FolderSelectorProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (folderId?: string) => void;
  currentFolderId?: string;        // Pre-selected folder
  title?: string;                  // Modal title
}

export default function FolderSelector({
  visible,
  onClose,
  onSelect,
  currentFolderId,
  title = 'Select Folder',
}: FolderSelectorProps) {
  const { folders, loading } = useFolders();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(currentFolderId || undefined);

  useEffect(() => {
    if (visible) {
      setSelectedFolderId(currentFolderId || undefined);
      setSearchQuery('');
    }
  }, [visible, currentFolderId]);

  if (!visible) return null;

  const getIconEmoji = (iconName?: string): string => {
    const iconMap: Record<string, string> = {
      folder: '📁',
      briefcase: '💼',
      book: '📚',
      palette: '🎨',
      'cart-shopping': '🛒',
      newspaper: '📰',
      utensils: '🍴',
      plane: '✈️',
      'laptop-code': '💻',
      heart: '❤️',
      star: '⭐',
      tag: '🏷️',
      bookmark: '🔖',
      file: '📄',
      image: '🖼️',
      video: '🎥',
    };
    return iconMap[iconName || 'folder'] || '📁';
  };

  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (folderId?: string) => {
    setSelectedFolderId(folderId);
  };

  const handleConfirm = () => {
    onSelect(selectedFolderId);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Search */}
              {folders.length > 10 && (
                <div className="mb-4">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search folders..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* No Folder Option */}
              <button
                onClick={() => handleSelect(undefined)}
                className={`w-full mb-3 p-3 rounded-lg border-2 text-left transition-all ${
                  selectedFolderId === null
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <span className="text-gray-400 text-lg">📁</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">No Folder</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Uncategorized</div>
                  </div>
                </div>
              </button>

              {/* Folder List */}
              <div className="space-y-2">
                {filteredFolders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'No folders found' : 'No folders available'}
                  </div>
                ) : (
                  filteredFolders.map((folder) => {
                    const isSelected = selectedFolderId === folder.id;
                    const iconBgColor = folder.color || '#6366F1';

                    return (
                      <button
                        key={folder.id}
                        onClick={() => handleSelect(folder.id)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg"
                            style={{ backgroundColor: iconBgColor }}
                          >
                            {getIconEmoji(folder.icon)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {folder.name}
                            </div>
                            {folder.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {folder.description}
                              </div>
                            )}
                            {folder.bookmarkCount !== undefined && (
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {folder.bookmarkCount} {folder.bookmarkCount === 1 ? 'bookmark' : 'bookmarks'}
                              </div>
                            )}
                          </div>
                          {isSelected && (
                            <div className="text-blue-500">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          <Button
            variant="outlined"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="flex-1"
          >
            Select
          </Button>
        </div>
      </div>
    </div>
  );
}

